import React from 'react';
import ReactDOM from 'react-dom';
import { appsManager, helpers } from '@kurtosys/ksys-app-template';
import { Provider } from 'mobx-react';
import App from './components/App/App';
import * as serviceWorker from './serviceWorker';
import { Manifest } from './configuration/Manifest';
import { getStoreContextForElement } from './utils/store';
import { EVENTS } from './utils/events';
import { CURRENT_SCRIPT } from './index';

const manifest = new Manifest();
const URL = window.location.href;
const hasManager = document.querySelectorAll('[data-ksys-app-template-id="ksys-app-manager"]').length > 0;
if (hasManager) {
	addManagerEventListeners();
}

function getApplicationCode() {
	const currentScript = CURRENT_SCRIPT;
	if (currentScript) {
		const { src } = currentScript as any;
		const regex = /\/apps\/(.*)\/main-ksys-(.*)\.js$/;
		const match = regex.exec(src);
		if (match && match.length > 1) {
			const [, applicationCode] = match;
			return applicationCode;
		}
	}
	return;
}

const readyForRender = (element: HTMLElement) => {
	return !(element.dataset && element.dataset.dynamicLoad === 'true' && element.dataset.embedAdded !== 'true');
};

function renderAppToTargets(elements?: HTMLElement[]) {
	const applicationCode = getApplicationCode();
	if (!elements) {
		elements = appsManager.utils.getElementsForManifest(manifest).filter(readyForRender);
	}
	if (hasManager) {
		elements = elements.filter((element: HTMLElement) => {
			return element && element.dataset && element.dataset.managerInitialized === 'true';
		});
	}

	const elementsForApplication = elements.filter((element) => {
		const { dataset } = element;
		const { applicationCode: elementApplicationCode } = dataset;
		return !applicationCode || !elementApplicationCode || applicationCode === elementApplicationCode;
	});

	if (elementsForApplication && elementsForApplication.length > 0) {
		for (const element of elementsForApplication) {
			if (!element.dataset || element.dataset.initialized !== 'true') {
				const storeContext = getStoreContextForElement(element, URL, manifest);
				const providerProps = (storeContext && storeContext.providerProps) || {};
				ReactDOM.render(
					<Provider { ...providerProps }>
						<App />
					</Provider>,
					element,
				);
				element.dataset.initialized = 'true';
			}
		}
	}
}

export function addEventListeners() {
	removeEventListeners();
	document.addEventListener(EVENTS.KSYS_APP_EMBED_ADDED, embedAddedHandler);
	document.addEventListener(EVENTS.KSYS_APP_PREVIEW_UPDATED, previewUpdatedHandler);
}

export function removeEventListeners() {
	document.removeEventListener(EVENTS.KSYS_APP_EMBED_ADDED, embedAddedHandler);
	document.addEventListener(EVENTS.KSYS_APP_PREVIEW_UPDATED, previewUpdatedHandler);
}

export function addManagerEventListeners() {
	document.addEventListener(EVENTS.KSYS_APP_MANAGER_INITIALIZED, managerInitializedHandler);
}

export function removeManagerEventListeners() {
	document.removeEventListener(EVENTS.KSYS_APP_MANAGER_INITIALIZED, managerInitializedHandler);
}

export function handleAppRender(event: Event, forceRender: boolean = false) {
	const { target } = event;
	if (target) {
		const { dataset } = target as HTMLElement;
		if (dataset) {
			const { ksysAppTemplateId, initialized } = dataset;
			console.log({ initialized });
			if ((initialized !== 'true' || forceRender) && ksysAppTemplateId === manifest.ksysAppTemplateId) {
				console.log(`New App render fired`, target);
				renderAppToTargets([target as any]);
			}
		}
	}
}

function managerInitializedHandler(event: Event) {
	handleAppRender(event);
}

function embedAddedHandler(event: Event) {
	const { target } = event;
	if (target) {
		const { dataset } = target as HTMLElement;
		dataset.embedAdded = 'true';
		handleAppRender(event);
	}
}

function previewUpdatedHandler(event: Event) {
	const target = event.target as HTMLElement;
	if (target) {
		const storeContext = getStoreContextForElement(target, URL, manifest);
		if (storeContext && storeContext.appStore && storeContext.appStore.loadPreviewContext) {
			storeContext.appStore.loadPreviewContext();
		}
	}
}

renderAppToTargets();

addEventListeners();
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
